import type { Move } from '../shared/types.js';

export interface DestinationChoice { nodeId: string; moves: Move[] }
export type BoardDestinationSelection =
  | { kind: 'none' }
  | { kind: 'submit'; move: Move }
  | { kind: 'choose'; moves: Move[] };

/** Uses server-authorized moves, retaining the first occurrence of each canonical key. */
export function movesForPiece(moves: Move[], pieceId: string): Move[] {
  const seen = new Set<string>();
  return moves.filter(move => {
    if (!move.pieceIds.includes(pieceId) || seen.has(move.key)) return false;
    seen.add(move.key);
    return true;
  });
}

/** Groups destination markers without combining different results or routes. */
export function destinationChoices(moves: Move[], pieceId: string): DestinationChoice[] {
  const choices = new Map<string, DestinationChoice>();
  for (const move of movesForPiece(moves, pieceId)) {
    const choice = choices.get(move.to);
    if (choice) choice.moves.push(move);
    else choices.set(move.to, { nodeId: move.to, moves: [move] });
  }
  return [...choices.values()];
}

/** A board click submits only an unambiguous move; every other valid choice stays explicit. */
export function chooseBoardDestination(moves: Move[], pieceId: string, nodeId: string): BoardDestinationSelection {
  const candidates = movesForPiece(moves, pieceId).filter(move => move.to === nodeId);
  if (!candidates.length) return { kind: 'none' };
  if (candidates.length === 1) return { kind: 'submit', move: candidates[0]! };
  return { kind: 'choose', moves: candidates };
}
